import { CheckOutlined, CopyOutlined } from '@ant-design/icons'
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popover,
  Progress,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import {
  DaoProjectItemType,
  DaoProposalDetailType,
  DaoProposalType,
  DaoTimelineMilestone,
  DaoVoterItemType,
} from 'Modules'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import MarkdownEditor from 'component/markdown-editor/markdown-editor'
import {
  DAO_DATETIME_FORMAT,
  DAO_MESSAGES,
  DAO_SALE_TIME_UNIT_OPTIONS,
  DaoAssetStatusEnum,
  DaoProposalStatusEnum,
  DaoSaleTimeUnitEnum,
  DaoTimelineStatusEnum,
  DaoVoteSupportEnum,
  SALE_TIME_UNIT_TO_DAYJS,
} from 'constants/dao-voting'
import { FEATURE } from 'constants/feature'
import { usePermission } from 'hooks/use-permission'
import { ProposalStatusTag } from 'modules/dao-voting/dao-status-tag'
import { getProposalNumber } from 'modules/dao-voting/utils'
import { logError } from 'utils/logger'

interface ProposalDetailModalProps {
  open: boolean
  project: DaoProjectItemType
  proposal?: DaoProposalType | null
  onClose: () => void
  /** open Edit form (Scheduled only) */
  onEdit: (proposal: DaoProposalType) => void
}

/** FE fallback titles per milestone key (BE label may carry extra info) */
const TIMELINE_TITLES: Record<string, string> = {
  PUBLISHED_ONCHAIN: 'Published onchain',
  VOTING_PERIOD_STARTED: 'Voting period started',
  VOTING_PERIOD_ENDED: 'Voting period ended',
  PROPOSAL_RESULT: 'Proposal result',
  ASSET_SALE_PERIOD: 'Asset sale period',
  SALE_RESULT: 'Sale result',
  CLAIMS_OPEN: 'Claims open',
  CLAIM_PERIOD_ENDED: 'Claim period ended',
}

const milestoneTitle = (m: DaoTimelineMilestone) => TIMELINE_TITLES[m.key] || m.label || m.key

const milestoneSub = (m: DaoTimelineMilestone) => {
  if (m.key === 'ASSET_SALE_PERIOD') {
    if (m.rangeStart && m.rangeEnd) {
      // spec (5): "Est. {Voting End} - {Voting End + Estimated Sale Time}"
      return `Est. ${dayjs(m.rangeStart).format(DAO_DATETIME_FORMAT)} – ${dayjs(m.rangeEnd).format(
        DAO_DATETIME_FORMAT
      )}`
    }
    return 'Est. duration not set'
  }
  if (m.timestamp) {
    const ts = dayjs(m.timestamp).format(DAO_DATETIME_FORMAT)
    // e.g. "Passed at Jul 28, 2026, 9:00 AM" / "Sale failed at ..."
    return m.label && m.label !== milestoneTitle(m) ? `${m.label} at ${ts}` : ts
  }
  return 'Date TBD'
}

/** spec (4): dynamic result label - "Passed" green, "Rejected" red */
const milestoneResultClass = (m: DaoTimelineMilestone) => {
  if (m.key !== 'PROPOSAL_RESULT' || !m.label) return ''
  if (m.label === 'Passed') return 'result-passed'
  if (m.label === 'Rejected') return 'result-rejected'
  return ''
}

const CLAMP_LENGTH = 220

/** Markdown content with View more / View less toggle (SRS 5.5) */
const ClampText = ({ text }: { text?: string }) => {
  const [expanded, setExpanded] = useState(false)
  if (!text) return <>--</>
  const needClamp = text.length > CLAMP_LENGTH
  return (
    <>
      <div style={needClamp && !expanded ? { maxHeight: 120, overflow: 'hidden' } : undefined}>
        <MarkdownEditor disabled value={text} placeholder="--" />
      </div>
      {needClamp ? (
        <a onClick={() => setExpanded((prev) => !prev)}>{expanded ? 'View less' : 'View more'}</a>
      ) : null}
    </>
  )
}

/** Proposal Detail modal - Overview & Configuration + Voters (SRS 5.5, 5.6) */
const ProposalDetailModal = ({
  open,
  project,
  proposal,
  onClose,
  onEdit,
}: ProposalDetailModalProps) => {
  // MEYREAL matrix: ADMIN views only - editing needs CRUD (SUPER_ADMIN / OPERATOR_MEYREAL)
  const { canCRUD } = usePermission()
  const canManage = canCRUD(FEATURE.DAO)

  const [detail, setDetail] = useState<DaoProposalDetailType | null>(null)
  const [voters, setVoters] = useState<DaoVoterItemType[]>([])
  const [timeline, setTimeline] = useState<DaoTimelineMilestone[]>([])
  const [saleTimeOpen, setSaleTimeOpen] = useState(false)
  const [saleTimeSubmitting, setSaleTimeSubmitting] = useState(false)
  const [saleTimeForm] = Form.useForm()
  const estimatedSaleTime = Form.useWatch('estimatedSaleTime', saleTimeForm)
  const estimatedSaleTimeUnit = Form.useWatch('estimatedSaleTimeUnit', saleTimeForm)
  const [totalVotes, setTotalVotes] = useState(0)
  const [votersTotal, setVotersTotal] = useState(0)
  const [votersPage, setVotersPage] = useState(1)
  const [votersLimit, setVotersLimit] = useState(10)
  const [searchValue, setSearchValue] = useState('')
  const [keyword, setKeyword] = useState('')
  const [loading, setLoading] = useState(false)

  const getDetail = useCallback(async () => {
    if (!proposal) return
    try {
      const res = await AppAdmin.getDaoVotingProposalDetail(proposal._id)
      setDetail(res.data?.data || null)
    } catch (error) {
      logError('Error fetching proposal detail: ', error)
      setDetail(null)
    }
  }, [proposal])

  const getTimeline = useCallback(async () => {
    if (
      !proposal ||
      proposal.status === DaoProposalStatusEnum.DRAFT ||
      proposal.status === DaoProposalStatusEnum.PUBLISHING
    ) {
      return
    }
    try {
      const res = await AppAdmin.getDaoVotingProposalTimeline(proposal._id)
      // spec: milestones sorted ascending by order
      setTimeline([...(res.data?.data?.milestones || [])].sort((a, b) => a.index - b.index))
    } catch (error) {
      logError('Error fetching proposal timeline: ', error)
      setTimeline([])
    }
  }, [proposal])

  const getVoters = useCallback(async () => {
    if (
      !proposal ||
      proposal.status === DaoProposalStatusEnum.DRAFT ||
      proposal.status === DaoProposalStatusEnum.PUBLISHING
    ) {
      return
    }
    try {
      setLoading(true)
      const res = await AppAdmin.getDaoVotingProposalVoters(proposal._id, {
        page: votersPage,
        limit: votersLimit,
        search: keyword,
      })
      const data = res.data?.data
      setVoters(data?.results || [])
      setTotalVotes(data?.totalVotes || 0)
      setVotersTotal(data?.totalResults || 0)
    } catch (error) {
      logError('Error fetching voters: ', error)
    } finally {
      setLoading(false)
    }
  }, [proposal, votersPage, votersLimit, keyword])

  useEffect(() => {
    if (open) {
      setSearchValue('')
      setKeyword('')
      setVotersPage(1)
      setDetail(null)
    }
  }, [open, proposal?._id])

  useEffect(() => {
    if (open) {
      getDetail()
      getVoters()
      getTimeline()
    }
  }, [open, getDetail, getVoters, getTimeline])

  // Live results come pre-computed from GET /admin/dao-voting/proposals/:id
  const tally = useMemo(
    () => ({
      totalVoted: detail?.votedCount ?? 0,
      forPercent: detail?.forPercent ?? 0,
      againstPercent: detail?.againstPercent ?? 0,
      participation: detail?.participationPercent ?? 0,
      notVoted: detail?.notVotedCount ?? 0,
      eligible: detail?.eligibleNftCount ?? project.totalSupply,
    }),
    [detail, project.totalSupply]
  )

  const timeLeft = useMemo(() => {
    const diff = detail?.timeLeftMs ?? 0
    if (diff <= 0) return '0d 0h left'
    const days = Math.floor(diff / (24 * 60 * 60 * 1000))
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
    return `${days}d ${hours}h left`
  }, [detail])

  if (!proposal) return null

  const isScheduled = proposal.status === DaoProposalStatusEnum.SCHEDULED
  const isPublishing = proposal.status === DaoProposalStatusEnum.PUBLISHING
  const isActive = proposal.status === DaoProposalStatusEnum.ACTIVE
  const isPassed = proposal.status === DaoProposalStatusEnum.PASSED
  const isRejected = proposal.status === DaoProposalStatusEnum.REJECTED

  // spec (5): Edit shows while asset = Voting (proposal Scheduled/Active) or
  // Awaiting Sale Result; hidden for Voting+Rejected and from Sale Success/
  // Sale Failed onwards
  const canEditSaleTime =
    canManage &&
    ((project.assetStatus === DaoAssetStatusEnum.VOTING && (isScheduled || isActive)) ||
      project.assetStatus === DaoAssetStatusEnum.AWAITING_SALE_RESULT)

  const voteColumns: ColumnsType<DaoVoterItemType> = [
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (address) => (
        <Space size={4}>
          <LinkAddress address={address} render={(short) => <span>{short}</span>} />
          <ButtonCopy title="Copy" text={address} icon={<CopyOutlined />} />
        </Space>
      ),
    },
    {
      title: 'PTO Voted',
      dataIndex: 'ptoVoted',
      key: 'ptoVoted',
      render: (value) => `${value} PTO`,
    },
    {
      title: 'Vote',
      dataIndex: 'support',
      key: 'support',
      render: (support: DaoVoteSupportEnum) =>
        support === DaoVoteSupportEnum.FOR ? (
          <Tag color="green">For</Tag>
        ) : (
          <Tag color="error">Against</Tag>
        ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'votedAt',
      key: 'votedAt',
      render: (value) => (value ? dayjs(value).format(DAO_DATETIME_FORMAT) : '--'),
    },
    {
      title: 'Transactions',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (txHash?: string) => (txHash ? <LinkAddress address={txHash} type="tx" /> : '--'),
    },
  ]

  const overviewTab = (
    <div className="dao-detail-columns">
      <div className="dao-detail-panel">
        <div className="dao-field-block">
          <div className="dao-field-label">Description</div>
          <ClampText text={detail?.description ?? proposal.description} />
        </div>
        <div className="dao-field-block">
          <div className="dao-field-label">Terms & Conditions</div>
          <ClampText text={detail?.termsAndConditions ?? proposal.terms} />
        </div>

        <div className="dao-field-box">
          <div className="dao-field-label">Discussion Room</div>
          {detail?.discussionRoomLinks?.length
            ? detail.discussionRoomLinks.map((link) => (
                <div key={link}>
                  <a href={link} target="_blank" rel="noreferrer">
                    {link}
                  </a>
                </div>
              ))
            : '--'}
        </div>

        <div className="dao-field-box">
          <div className="dao-field-label">Proposal Contract Address</div>
          {proposal.contractAddress ? (
            <Space>
              <span className="value">{proposal.contractAddress}</span>
              <ButtonCopy title="Copy" text={proposal.contractAddress} icon={<CopyOutlined />} />
            </Space>
          ) : (
            'Not deployed'
          )}
        </div>

        <div className="dao-field-grid">
          <div>
            <div className="dao-field-label">Voting Start</div>
            <div className="value">
              {proposal.votingStart
                ? dayjs(proposal.votingStart).format(DAO_DATETIME_FORMAT)
                : '--'}
            </div>
          </div>
          <div>
            <div className="dao-field-label">Voting End</div>
            <div className="value">
              {proposal.votingEnd ? dayjs(proposal.votingEnd).format(DAO_DATETIME_FORMAT) : '--'}
            </div>
          </div>
          <div>
            <div className="dao-field-label">Quorum Required</div>
            <div className="value">{proposal.quorum != null ? `${proposal.quorum}%` : '--'}</div>
          </div>
          <div>
            <div className="dao-field-label">Threshold Required</div>
            <div className="value">
              {proposal.threshold != null ? `${proposal.threshold}%` : '--'}
            </div>
          </div>
        </div>
      </div>

      <div className="dao-detail-panel">
        <div className="dao-live-title">Live Results</div>
        {isScheduled || isPublishing ? (
          <div>Voting has not started yet</div>
        ) : (
          <>
            <div className="dao-progress-row">
              <div className="dao-progress-head for">
                <span>For</span>
                <span>{tally.forPercent}%</span>
              </div>
              <Progress percent={tally.forPercent} showInfo={false} strokeColor="#52c41a" />
            </div>
            <div className="dao-progress-row">
              <div className="dao-progress-head against">
                <span>Against</span>
                <span>{tally.againstPercent}%</span>
              </div>
              <Progress percent={tally.againstPercent} showInfo={false} strokeColor="#ff4d4f" />
            </div>

            <div className="dao-participation">
              <div className="dao-participation-head">
                <span className="dao-field-label" style={{ marginBottom: 0 }}>
                  Participation
                </span>
                <span className="pct">{tally.participation}%</span>
              </div>
              <Progress
                percent={tally.participation}
                showInfo={false}
                strokeColor="#1677ff"
                size="small"
              />
              <div className="dao-participation-sub">
                <b>{tally.totalVoted}</b> voted · <b>{tally.notVoted}</b> not voted
                <br />
                of {tally.eligible} eligible
              </div>
            </div>

            {isActive ? (
              <div className="dao-result-box time-left">
                <div className="label">Time Left</div>
                <div className="value">{timeLeft}</div>
              </div>
            ) : null}
            {isPassed ? (
              <div className="dao-result-box passed">
                <div className="label">Final Result</div>
                <div className="value">Passed ✓</div>
              </div>
            ) : null}
            {isRejected ? (
              <div className="dao-result-box rejected">
                <div className="label">Final Result</div>
                <div className="value">Rejected ✕</div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )

  const onSearchVoters = () => {
    setVotersPage(1)
    setKeyword(searchValue.trim())
  }

  /** the clear icon also drops the applied keyword so the full list comes back */
  const onChangeSearchVoters = (value: string) => {
    setSearchValue(value)
    if (!value) {
      setVotersPage(1)
      setKeyword('')
    }
  }

  const votersTab = (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Input
          placeholder="Search by wallet address"
          maxLength={255}
          allowClear
          value={searchValue}
          onChange={(e) => onChangeSearchVoters(e.target.value)}
          onPressEnter={onSearchVoters}
          style={{ width: 280 }}
        />
        <Button type="primary" onClick={onSearchVoters}>
          Search
        </Button>
        <span style={{ color: 'rgba(0,0,0,0.55)' }}>
          Showing <b>{voters.length}</b> of <b>{totalVotes}</b> votes
        </span>
      </Space>
      <Table
        columns={voteColumns}
        dataSource={voters}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: votersPage,
          pageSize: votersLimit,
          total: votersTotal,
          showSizeChanger: false,
          onChange: (nextPage, nextSize) => {
            setVotersPage(nextSize !== votersLimit ? 1 : nextPage)
            setVotersLimit(nextSize)
          },
        }}
        locale={{ emptyText: 'No votes cast yet' }}
      />
    </div>
  )

  /** Edit "Asset sale period": PATCH the estimated sale duration */
  const handleSaveSaleTime = async () => {
    if (!proposal) return
    try {
      const values = await saleTimeForm.validateFields()
      setSaleTimeSubmitting(true)
      await AppAdmin.updateDaoVotingEstimatedSaleTime(proposal._id, {
        estimatedSaleTime: values.estimatedSaleTime,
        estimatedSaleTimeUnit: values.estimatedSaleTimeUnit,
      })
      message.success('Update Estimated Sale Time successfully')
      setSaleTimeOpen(false)
      getTimeline()
    } catch (error: any) {
      if (error?.errorFields) return
      logError('Error updating estimated sale time: ', error)
      message.error(error?.response?.data?.message || 'Failed to update estimated sale time')
    } finally {
      setSaleTimeSubmitting(false)
    }
  }

  /** spec (5) popover: number + unit, From (locked) = Voting End, To = From + value */
  const renderSaleTimePopover = (m: DaoTimelineMilestone) => {
    const from = m.rangeStart || (proposal?.votingEnd ? dayjs(proposal.votingEnd) : null)
    const fromLabel = from ? dayjs(from).format(DAO_DATETIME_FORMAT) : '--'
    const toLabel =
      from && estimatedSaleTime && estimatedSaleTime > 0 && estimatedSaleTimeUnit
        ? dayjs(from)
            .add(
              estimatedSaleTime,
              SALE_TIME_UNIT_TO_DAYJS[estimatedSaleTimeUnit as DaoSaleTimeUnitEnum]
            )
            .format(DAO_DATETIME_FORMAT)
        : '--'

    return (
      <div style={{ width: 300 }}>
        <Form
          form={saleTimeForm}
          layout="vertical"
          initialValues={{ estimatedSaleTimeUnit: DaoSaleTimeUnitEnum.DAYS }}
        >
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="estimatedSaleTime"
              style={{ flex: 1, marginBottom: 8 }}
              rules={[
                { required: true, message: 'Estimated Sale Time is required' },
                {
                  validator: (_, value?: number) =>
                    value != null && value < 1
                      ? Promise.reject(new Error(DAO_MESSAGES.E06('Estimated Sale Time')))
                      : Promise.resolve(),
                },
              ]}
            >
              <InputNumber placeholder="e.g. 3" style={{ width: '100%' }} min={1} precision={0} />
            </Form.Item>
            <Form.Item name="estimatedSaleTimeUnit" style={{ width: 110, marginBottom: 8 }}>
              <Select options={DAO_SALE_TIME_UNIT_OPTIONS} />
            </Form.Item>
          </Space.Compact>
        </Form>
        <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)', marginBottom: 4 }}>
          From (locked): <b>{fromLabel}</b>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)', marginBottom: 12 }}>
          To: <b>{toLabel}</b>
        </div>
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setSaleTimeOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            type="primary"
            loading={saleTimeSubmitting}
            onClick={handleSaveSaleTime}
          >
            Save
          </Button>
        </Space>
      </div>
    )
  }

  const timelineTab = (
    <div className="dao-detail-panel">
      <div className="dao-timeline">
        {timeline.map((m) => {
          const statusClass =
            m.status === DaoTimelineStatusEnum.DONE
              ? 'done'
              : m.status === DaoTimelineStatusEnum.ACTIVE
                ? 'active'
                : 'upcoming'
          return (
            <div key={m.index} className="dao-timeline-item">
              <span className={`dao-timeline-dot ${statusClass}`}>
                {m.status === DaoTimelineStatusEnum.DONE ? <CheckOutlined /> : m.index}
              </span>
              <div>
                <div className={`dao-timeline-title ${statusClass}`}>
                  {milestoneTitle(m)}
                  {m.key === 'ASSET_SALE_PERIOD' && m.editable && canEditSaleTime ? (
                    <Popover
                      open={saleTimeOpen}
                      onOpenChange={(next) => {
                        if (next) saleTimeForm.resetFields()
                        setSaleTimeOpen(next)
                      }}
                      trigger="click"
                      placement="right"
                      content={renderSaleTimePopover(m)}
                    >
                      <a style={{ marginLeft: 10, fontSize: 13, fontWeight: 400 }}>Edit</a>
                    </Popover>
                  ) : null}
                </div>
                <div className={`dao-timeline-sub ${statusClass} ${milestoneResultClass(m)}`}>
                  {milestoneSub(m)}
                </div>
              </div>
            </div>
          )
        })}
        {!timeline.length ? <div style={{ color: 'rgba(0,0,0,0.45)' }}>No timeline yet</div> : null}
      </div>
    </div>
  )

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={960}
      footer={null}
      className="dao-proposal-detail"
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingRight: 40 }}>
          <Space direction="vertical" size={2}>
            <span>
              Proposal #{getProposalNumber(project, proposal)}: {proposal.title}
            </span>
            <span style={{ fontSize: 13, fontWeight: 400, color: 'rgba(0,0,0,0.55)' }}>
              Linked PTO: <b>{project.fullName}</b>
            </span>
          </Space>
          <Space>
            {canManage && (isScheduled || isPublishing || !proposal.contractAddress) ? (
              <Button size="small" onClick={() => onEdit(proposal)}>
                Edit
              </Button>
            ) : null}
            <ProposalStatusTag status={proposal.status} />
          </Space>
        </div>
      }
    >
      <Tabs
        defaultActiveKey="overview"
        items={[
          { key: 'overview', label: 'Overview & Configuration', children: overviewTab },
          { key: 'voters', label: 'Voters', children: votersTab },
          { key: 'timeline', label: 'Timeline', children: timelineTab },
        ]}
      />
    </Modal>
  )
}

export default ProposalDetailModal
